import {
  Alert,
  Button,
  CircularProgress,
  Collapse,
  Popper,
} from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { StyledInput } from '../../UIComponents/StyledInput';
import { useKundmarkeContext } from '../../common/contexts/KundmarkeContext';
import {
  getFromStorage,
  removeStorageKey,
  removeStorageKeys,
  saveToStorage,
} from '../../common/lib/storage';
import { extractQueryParams } from '../../common/miscFunctions';
import { ErrorTypes } from '../../types/ErrorTypes';
import { FormError } from '../../types/FormError';
import { LoadState } from '../../types/LoadState';
import * as ForetagClient from '../clients/foretagClient';
import { formErrorMap } from '../lib/formErrorMap';
import { GrunduppgifterDto } from '../model/GrunduppgifterDto';
import { ForetagTransactions } from '../types/ForetagTransactions';
import styles from './ForetagSearchForm.module.css';
import { ForetagSearchResult } from './ForetagSearchResult/ForetagSearchResult';
import { InnehavInfoContainer } from './InnehavInfo/InnehavInfoContainer';
import { LedningInfoContainer } from './LedningInfo/LedningInfoContainer';

export enum ForetagStorageType {
  Ledning = 'ledningSearch',
  Grundinformation = 'grundinformationSearch',
  Innehav = 'fastighetsinnehavSearch',
}

export const ForetagSearchForm: React.FC = () => {
  const queryParams = extractQueryParams(window.location.search);

  const [organisationsnummer, setOrganisationsnummer] = useState(
    queryParams.organisationsnummer || ''
  );

  const [activeOrganisationsnummer, setActiveOrganisationsnummer] =
    useState('');
  const { kundmarke, setKundmarke, visaKundmarke } = useKundmarkeContext();
  const [searchLoadState, setSearchLoadState] = useState(LoadState.Idle);
  const [error, setError] = useState<FormError>();
  const [permissionsError, setPermissionsError] = useState<FormError>();
  const [searchResult, setSearchResult] = useState<GrunduppgifterDto>();
  const [permissions, setPermissions] = useState<ForetagTransactions[]>();
  const [anchorEl, setAnchorEl] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [infoRowsInitialKundmarke, setInfoRowsInitialKundmarke] = useState('');

  useEffect(() => {
    const storedSearchResult = getFromStorage<GrunduppgifterDto>(
      ForetagStorageType.Grundinformation
    );

    if (storedSearchResult) {
      if (queryParams.organisationsnummer) {
        removeStorageKeys(Object.values(ForetagStorageType));
      } else {
        setSearchResult(storedSearchResult);
        setFromCache(true);
        setActiveOrganisationsnummer(storedSearchResult.omfragadlista?.[0]?.id);
        setOrganisationsnummer(storedSearchResult.omfragadlista?.[0]?.id);
        setSearchLoadState(LoadState.Success);
      }
    }
  }, []);

  const validateOrganisationsnummer = (organisationsnummer: string): boolean =>
    /^(\d{6}-?\d{4})$/.test(organisationsnummer);

  const sanitizeOrganisationsnummer = (organisationsnummer: string): string => {
    const trimmedOrgnr = organisationsnummer.trim();
    return trimmedOrgnr.includes('-')
      ? trimmedOrgnr
      : `${trimmedOrgnr.slice(0, 6)}-${trimmedOrgnr.slice(6)}`;
  };

  const sokGrunduppgifter = (
    e: FormEvent<HTMLFormElement>,
    organisationsnummer: string
  ) => {
    e.preventDefault();
    setActiveOrganisationsnummer(
      sanitizeOrganisationsnummer(organisationsnummer)
    );
  };

  useEffect(() => {
    const searchForetag = async (orgnr: string) => {
      if (!orgnr || !validateOrganisationsnummer(orgnr)) {
        setError(formErrorMap[ErrorTypes.ValidationFailed]);
        setSearchLoadState(LoadState.Error);
        return;
      }
      setSearchResult(undefined);
      removeStorageKey(ForetagStorageType.Grundinformation);
      setSearchLoadState(LoadState.Loading);
      setError(undefined);
      const result = await ForetagClient.getGrunduppgifter(orgnr, kundmarke);

      if (result.success) {
        saveToStorage<GrunduppgifterDto>(
          ForetagStorageType.Grundinformation,
          result.data
        );
        setInfoRowsInitialKundmarke(kundmarke);
        setSearchResult(result.data);
        setSearchLoadState(LoadState.Success);
      } else {
        setSearchLoadState(LoadState.Error);
        setError(
          {
            403: formErrorMap[ErrorTypes.Forbidden],
            404: formErrorMap[ErrorTypes.ForetagNotFound],
          }[result.statusCode] || formErrorMap[ErrorTypes.UnknownError]
        );
      }
    };

    if (fromCache) {
      return setFromCache(false);
    }

    if (activeOrganisationsnummer) {
      searchForetag(activeOrganisationsnummer);
    }
  }, [activeOrganisationsnummer]);

  useEffect(() => {
    const getPermissions = async () => {
      try {
        const result = await ForetagClient.getUserPermission();
        if (result.success) {
          setPermissions(result.data.transaktioner);
        } else {
          setPermissionsError({
            severity: 'warning',
            text: 'Misslyckades med att hämta tillåtna köp.',
          });
        }
      } catch {
        setPermissionsError({
          severity: 'warning',
          text: 'Misslyckades med att hämta tillåtna köp.',
        });
      }
    };
    getPermissions();
  }, []);

  const foretag = searchResult?.omfragadlista?.[0];
  const companyName = foretag?.namnlista?.namn?.[0]?.value;
  const buyDisabled =
    permissions && !permissions.includes(ForetagTransactions.IPM_SYNA_GRUFO);

  return (
    <>
      {permissionsError && (
        <Alert severity={permissionsError.severity} sx={{ mb: 2 }}>
          {permissionsError.text}
        </Alert>
      )}
      <form onSubmit={(e) => sokGrunduppgifter(e, organisationsnummer)}>
        <StyledInput
          label="Organisationsnummer"
          value={organisationsnummer}
          inputProps={{ tabIndex: 1 }}
          onChange={(e) => setOrganisationsnummer(e.currentTarget.value)}
        />
        <StyledInput
          label="Kundmärke"
          value={kundmarke}
          disabled={!visaKundmarke}
          inputProps={{ tabIndex: 2 }}
          onChange={(e) => setKundmarke(e.currentTarget.value)}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={buyDisabled || searchLoadState === LoadState.Loading}
        >
          {searchLoadState === LoadState.Loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <div
              style={{ pointerEvents: 'all' }}
              onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
              onMouseLeave={() => setAnchorEl(null)}
            >
              Sök
              {buyDisabled && (
                <Popper open={!!anchorEl} anchorEl={anchorEl}>
                  <Alert severity="info">
                    Ditt avtal saknar den transaktionen som krävs för denna
                    sökning.
                  </Alert>
                </Popper>
              )}
            </div>
          )}
        </Button>

        {searchLoadState === LoadState.Error && error && (
          <Alert severity={error.severity}>{error.text}</Alert>
        )}
      </form>
      <Collapse in={searchLoadState === LoadState.Success}>
        <ForetagSearchResult foretag={foretag} />
        <div className={styles.additionalDataWrapper}>
          <LedningInfoContainer
            organisationsnummer={activeOrganisationsnummer}
            permissions={permissions}
            initialKundmarke={infoRowsInitialKundmarke}
            companyName={companyName}
            cacheKey={ForetagStorageType.Ledning}
          />
          <InnehavInfoContainer
            organisationsnummer={activeOrganisationsnummer}
            initialKundmarke={infoRowsInitialKundmarke}
            permissions={permissions}
            companyName={companyName}
            cacheKey={ForetagStorageType.Innehav}
          />
        </div>
      </Collapse>
    </>
  );
};
